/**
 * Where an employee stands in their own review cycle.
 *
 * The design book draws a five-stage rail across the top of this screen, and
 * the temptation is to hard-code which lamp is lit. Every stage here is derived
 * from the review's own status instead, because the rail is the employee's
 * answer to "whose turn is it" — and a rail that says "self-review due" to
 * somebody who submitted theirs last week is worse than no rail.
 *
 * Two facts about a goal are kept apart here, and the whole file follows from
 * the distinction:
 *
 *   PROGRESS   how much is done. Computed from the key results, so "completed"
 *              is something a reader can check rather than something the
 *              subject asserted. Goal attainment feeds the appraisal rating,
 *              which is why it has to be evidence.
 *   CHECK-IN   how it is going. A goal at 40% might be perfectly on track or
 *              completely stuck, and no arithmetic on the key results can tell
 *              the difference. Only the person doing the work knows, so this
 *              half is theirs, and nothing infers it.
 */

import { shortDate } from "../../admin-main/performance/performanceFacts";

/** The employee's own read, and how it is shown. */
export const CHECK_IN = {
  on_track: { label: "On track", tone: "success" },
  at_risk: { label: "At risk", tone: "warning" },
  blocked: { label: "Blocked", tone: "danger" },
};

export const CHECK_IN_OPTIONS = ["on_track", "at_risk", "blocked"];

/**
 * The check-in, or nothing.
 *
 * Absent is a real state and not the same as "on track": somebody who has not
 * said how it is going has not claimed it is fine, and rendering silence as
 * reassurance is how a stuck goal stays invisible until the review.
 */
export const checkInOf = (goal) => {
  const state = goal?.checkIn?.state;
  if (!state || !CHECK_IN[state]) return null;
  return { state, note: goal.checkIn.note || "", at: goal.checkIn.at, ...CHECK_IN[state] };
};

/*
 * "target 30 Jun 2026" — set when the goal was agreed, and the manager's to move.
 *
 * Carries the year, where `shortDate` deliberately does not. A deadline inside
 * a cycle is unambiguous without one; a goal's target is not, because goals sit
 * beside others from earlier cycles and "30 Jun" alone would read as this year's.
 * Months are spelled from a table rather than through toLocaleDateString, which
 * renders September as "Sept" and nothing else abbreviated.
 */
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export const targetLabel = (goal) => {
  const value = goal?.periodEnd;
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  let y, mo, d;
  if (m) {
    [, y, mo, d] = m;
  } else {
    const parsed = new Date(String(value));
    if (Number.isNaN(parsed.getTime())) return null;
    y = parsed.getUTCFullYear();
    mo = parsed.getUTCMonth() + 1;
    d = parsed.getUTCDate();
  }
  const i = Number(mo) - 1;
  if (i < 0 || i > 11) return null;
  return `target ${Number(d)} ${MONTHS[i]} ${y}`;
};

/**
 * How far along a goal is.
 *
 * The server computes `progress` from the key results; this prefers it and
 * falls back to deriving the same figure, so a goal that arrives without it
 * still draws a bar rather than a permanent zero. Each key result counts once
 * regardless of its unit — a goal measured in both "findings" and "days" cannot
 * be averaged any other way — and one that has overshot its target is capped,
 * because a goal 140% done is still done.
 */
export const goalProgress = (goal) => {
  if (typeof goal?.progress === "number") return Math.max(0, Math.min(100, goal.progress));
  const krs = (goal?.keyResults || []).filter((k) => Number(k?.target) > 0);
  if (!krs.length) return 0;
  const total = krs.reduce((sum, k) => {
    const share = (Number(k.current) || 0) / Number(k.target);
    return sum + Math.max(0, Math.min(1, share));
  }, 0);
  return Math.round((total / krs.length) * 100);
};

/** Goals belonging to one cycle. A goal from last year is not evidence about this one. */
export const goalsInCycle = (goals = [], cycleId) => {
  if (!cycleId) return goals || [];
  return (goals || []).filter((g) => {
    const cycle = g?.reviewCycle?._id || g?.reviewCycle;
    return String(cycle) === String(cycleId);
  });
};

/**
 * The counter band: the three live states, and how many goals there are.
 *
 * Cancelled goals are excluded from every figure: a goal that was called off is
 * not a goal at 0%, and counting it would drag the numbers down for somebody
 * who did nothing wrong. It is reported separately so the exclusion is visible
 * rather than silent.
 *
 * `atRisk` is counted but does not get a counter of its own — it belongs to the
 * goals that are flagged, not to a tally at the top. It is here because the
 * screen needs to know whether to say anything about it at all.
 */
export const goalTotals = (goals = []) => {
  const live = (goals || []).filter((g) => g?.status !== "cancelled");
  const count = (key) => live.filter((g) => g.status === key).length;
  return {
    total: live.length,
    completed: count("completed"),
    inProgress: count("in_progress"),
    notStarted: count("not_started"),
    cancelled: (goals || []).length - live.length,
    atRisk: live.filter((g) => ["at_risk", "blocked"].includes(g?.checkIn?.state)).length,
    average: live.length
      ? Math.round(live.reduce((sum, g) => sum + goalProgress(g), 0) / live.length)
      : 0,
  };
};

/**
 * The cycle this screen should open on, without asking for the cycle list.
 *
 * `/review-cycle` is an admin endpoint, so an employee reading it gets a 403
 * and the whole rail would silently never appear for the people it was built
 * for. Reviews arrive with their cycle populated, so the cycle is taken from
 * there: the active one if any review names it, otherwise the review that is
 * still open, otherwise the most recent. Goals also carry `reviewCycle`, which
 * covers the case where goals are agreed before the cycle is activated and no
 * review exists yet.
 */
export const currentReview = (reviews = []) => {
  const rows = (reviews || []).filter(Boolean);
  if (!rows.length) return null;
  const active = rows.find((r) => r?.cycle?.status === "active");
  if (active) return active;
  const open = rows.find((r) => r?.status !== "finalized");
  if (open) return open;
  return [...rows].sort(
    (a, b) => Date.parse(b?.finalizedAt || b?.updatedAt || 0) - Date.parse(a?.finalizedAt || a?.updatedAt || 0)
  )[0];
};

/** The cycle to render against, from the review if there is one, else the goals. */
export const cycleFor = (reviews = [], goals = []) => {
  const cycle = currentReview(reviews)?.cycle;
  if (cycle) return cycle;
  const withCycle = (goals || []).find((g) => g?.reviewCycle?._id);
  return withCycle?.reviewCycle || null;
};

/**
 * The employee's review for a cycle, if one has been generated.
 *
 * Reviews only exist once a cycle is activated, so its absence is a real state
 * — "the cycle has not opened yet" — rather than a loading failure.
 */
export const reviewForCycle = (reviews = [], cycleId) => {
  if (!cycleId) return (reviews || [])[0] || null;
  return (
    (reviews || []).find((r) => String(r?.cycle?._id || r?.cycle) === String(cycleId)) || null
  );
};

/**
 * The cycle's questions for one side of the review.
 *
 * `askedOf` is the cycle's own decision, so a prompt put to both sides appears
 * for both. Filtering here rather than at the point of render keeps the modal
 * and the "have you finished" count reading the same list.
 */
export const questionsFor = (cycle, side) =>
  (cycle?.questions || []).filter((q) => q?.askedOf === "both" || q?.askedOf === side);

/** Answers keyed by question id, for seeding a form from a saved review. */
export const answersById = (assessment) => {
  const out = {};
  (assessment?.answers || []).forEach((a) => {
    if (a?.question) out[String(a.question)] = a.text || "";
  });
  return out;
};

/**
 * How much of the self-review is done, and whether it can be submitted.
 *
 * Only the required questions gate submission — an optional one left blank is a
 * choice, not an omission — but the count reports every question, because "2 of
 * 3 answered" should match what the reader can see on the page.
 */
export const reviewProgress = (questions = [], answers = {}) => {
  const filled = (q) => Boolean((answers[String(q._id)] || "").trim());
  const required = questions.filter((q) => q.required !== false);
  return {
    total: questions.length,
    answered: questions.filter(filled).length,
    missing: required.filter((q) => !filled(q)).length,
    canSubmit: questions.length > 0 && required.every(filled),
  };
};

/**
 * The one sentence under the heading: which cycle, and what it wants next.
 */
export const cycleLine = ({ cycle, review, manager } = {}) => {
  if (!cycle) return "No review cycle is running";
  const parts = [cycle.name];
  const managerName = manager?.fname
    ? `${manager.fname} ${manager.lname || ""}`.trim()
    : null;
  if (managerName) parts.push(`agreed with ${managerName}`);
  if (!review) parts.push("cycle not open to you yet");
  else if (review.status === "pending") {
    const due = shortDate(cycle.dueDate);
    parts.push(due ? `self-review closes ${due}` : "self-review is open");
  } else if (review.status === "self_completed") parts.push("waiting on your manager");
  else if (review.status === "manager_completed") parts.push("awaiting sign-off");
  else if (review.status === "finalized") parts.push("closed");
  return parts.join(" · ");
};

/**
 * The most recent cycle that actually reached a rating.
 *
 * Only finalized reviews qualify: a cycle that closed without being signed off
 * has no rating to report, and showing the cycle name with an empty rating
 * reads as one that was lost rather than one that never finished.
 */
export const lastFinalisedReview = (reviews = [], currentCycleId) =>
  (reviews || [])
    .filter(
      (r) =>
        r?.status === "finalized" &&
        String(r?.cycle?._id || r?.cycle) !== String(currentCycleId)
    )
    .sort((a, b) => Date.parse(b?.finalizedAt || b?.updatedAt || 0) - Date.parse(a?.finalizedAt || a?.updatedAt || 0))[0] || null;
